import { atom } from 'jotai';
import type {
	LocationHash,
	SemanticSearchState,
	SemanticSearchTypeahead,
} from './src/types';

export const semanticSearchInitialState: SemanticSearchState = {
	searchFieldValue: '',
};
export const fieldValueAtom = atom('');
export const semanticSearchStringAtom = atom('');
export const isSemanticSearchCalledAtom = atom<boolean>(
	(get) => get(semanticSearchStringAtom).length > 0,
);

export const semanticSearchTypeaheadAtom = atom<SemanticSearchTypeahead>({
	semanticSearchResults: [],
	storefronts: [],
});

export const isSemanticSuggestionsOpenAtom = atom(false);

export const isTopCategoriesOpenAtom = atom(false);

export const suggestedCategoriesAtom = atom<{ categories: string[] }>({
	categories: [],
});

export interface ClassifierMetadata {
	/** Category code the classifier predicted for the current query, if any. */
	prediction: string | null;
	/**
	 * Confidence (probability) of the classifier's top prediction, 0–1.
	 * Null when the classifier returned no probabilities for the query.
	 */
	confidence: number | null;
}

/**
 * sessionStorage key used to persist classifier metadata across the full-page
 * navigation triggered by semantic search submit. Written by the semantic search
 * redirect handler (use-semantic-search.ts) immediately before
 * `window.location.href`, read and cleared here on the destination page.
 */
export const CLASSIFIER_METADATA_SESSION_KEY =
	'pending_semantic_classifier_metadata';

function getInitialClassifierMetadata(): ClassifierMetadata {
	try {
		if (typeof sessionStorage !== 'undefined') {
			const pending = sessionStorage.getItem(CLASSIFIER_METADATA_SESSION_KEY);
			if (pending) {
				const parsed = JSON.parse(pending) as ClassifierMetadata;
				sessionStorage.removeItem(CLASSIFIER_METADATA_SESSION_KEY);
				return parsed;
			}
		}
	} catch {
		// sessionStorage may be unavailable (SSR, private browsing restrictions)
	}
	return { prediction: null, confidence: null };
}

/**
 * Classifier output for the current free-text query, captured from the
 * `semanticSearch.classification` response while the user types (see
 * `use-semantic-suggestions`). The classifier confidence is not returned by the
 * results-page `liteThresholdSearch` query, so it must be persisted to
 * sessionStorage before the full-page navigation on search submit and restored
 * here on the destination page (see CLASSIFIER_METADATA_SESSION_KEY).
 */
export const classifierMetadataAtom = atom<ClassifierMetadata>(
	getInitialClassifierMetadata(),
);

/**
 * Retained for structural compatibility, but never written to.
 * The location picker UI was removed during the `newMarketplaceSearchBarV2`
 * experiment removal and is no longer present in the search bar UI.
 */
export const temporaryLocationHashAtom = atom<LocationHash | null>(null);

/**
 * Set by a page (e.g., Storefront) to lock the search bar to a specific location.
 * When non-null, this takes priority over temporaryLocationHashAtom (which is now unused,
 * retained only for structural compatibility after location picker removal).
 */
export const pageLocationHashAtom = atom<LocationHash | null>(null);

/**
 * Derived atom that resolves the effective location from Jotai sources.
 * Resolution within the atom: `pageLocationHashAtom` (page-injected) →
 * `temporaryLocationHashAtom` (compatibility-only fallback).
 *
 * Important: when consumers need a single unified location value the Redux
 * fallback (`state.location`) is applied in the `useResolvedLocation` hook —
 * the atom intentionally does not depend on Redux.
 */
export const resolvedLocationHashAtom = atom<LocationHash | null>(
	(get) => get(pageLocationHashAtom) ?? get(temporaryLocationHashAtom),
);

export const isOnSemanticSearchInExperimentAtom = atom(false);
